/* eslint-disable react/prop-types */
import React, { CSSProperties, forwardRef } from "react";

import { UiModalModel } from "../../models";

import { AnimationPhase, ModelContent, animationSpeed } from "./animations";

type OverlayProps = React.HTMLAttributes<HTMLDivElement> & {
    readonly animation: AnimationPhase;
    readonly transitionSpeed?: UiModalModel["content"]["transitionSpeed"];
    style?: CSSProperties;
};

export const Overlay = ({
    animation,
    transitionSpeed,
    style,
    ...props
}: OverlayProps) => {
    const overlayStyle: CSSProperties = {
        opacity: animation === "in" ? 1 : 0,
        // eslint-disable-next-line local/ensure-wac-token-usage
        backgroundColor: "rgba(0, 0, 0, 0.5)",
        overflow: "auto",
        position: "fixed",
        height: "100vh",
        width: "100vw",
        top: 0,
        left: 0,
        zIndex: 1000,
        transition: `opacity ${animationSpeed.getSpeed(
            transitionSpeed
        )}ms ease-out`,
        ...style
    };
    return <div style={overlayStyle} {...props} />;
};

type ModalContentContainerProps = React.HTMLAttributes<HTMLDivElement> & {
    readonly animation: AnimationPhase;
    readonly transitionType?: UiModalModel["content"]["transitionType"];
    readonly transitionSpeed?: UiModalModel["content"]["transitionSpeed"];
    style?: CSSProperties;
};

export const ModalContentContainer = forwardRef<
    HTMLDivElement,
    ModalContentContainerProps
>(({ animation, transitionType, transitionSpeed, style, ...props }, ref) => {
    const speed = animationSpeed.getSpeed(transitionSpeed);
    let containerStyle: CSSProperties = {
        alignItems: "center",
        justifyContent: "center",
        display: "flex",
        padding: "25px",
        position: "absolute",
        minHeight: "100vh",
        width: "100vw",
        top: 0,
        left: 0,
        transition: `all ${speed}ms ease-out`
    };

    // Apply transition-specific styles
    switch (transitionType) {
        case "SlideDown FadeOut":
        case "SlideDown InstantRemove":
            containerStyle = {
                ...containerStyle,
                transform:
                    animation === "in"
                        ? "translate3d(0, 0, 0)"
                        : "translate3d(0, -25%, 0)",
                opacity: animation === "in" ? 1 : 0
            };
            break;
        case "SlideDown SlideBelow":
            containerStyle = {
                ...containerStyle,
                transform:
                    animation === "in"
                        ? "translate3d(0, 0, 0)"
                        : "translate3d(0, 25%, 0)"
            };
            break;
        case "ScaleUp ScaleDown":
            containerStyle = {
                ...containerStyle,
                transform: animation === "in" ? "scale(1)" : "scale(0)"
            };
            break;
        default:
            containerStyle = {
                ...containerStyle,
                opacity: animation === "in" ? 1 : 0
            };
    }

    return <div ref={ref} style={{ ...containerStyle, ...style }} {...props} />;
});

type ModalContentProps = React.HTMLAttributes<HTMLDivElement> &
    ModelContent & {
        style?: CSSProperties;
    };

export const ModalContent = ({
    modalSize,
    style,
    ...props
}: ModalContentProps) => {
    let sizeStyle: CSSProperties = {};
    switch (modalSize) {
        case "Large":
            sizeStyle = { width: "1140px", maxWidth: "100%" };
            break;
        case "Medium":
            sizeStyle = { width: "720px", maxWidth: "100%" };
            break;
        case "Small":
            sizeStyle = { width: "540px", maxWidth: "100%" };
            break;
    }
    return (
        <div
            style={{
                position: "relative",
                maxWidth: "100%",
                ...sizeStyle,
                ...style
            }}
            {...props}
        />
    );
};

type CloseButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement> & {
    style?: CSSProperties;
};

export const CloseButton = ({ style, ...props }: CloseButtonProps) => {
    const buttonStyle: CSSProperties = {
        appearance: "none",
        // eslint-disable-next-line local/ensure-wac-token-usage
        backgroundColor: "#ffffff",
        border: "none",
        borderRadius: "50%",
        cursor: "pointer",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 0,
        position: "absolute",
        textAlign: "center",
        top: "-25px",
        right: "-25px",
        height: "50px",
        width: "50px",
        zIndex: 1,
        ...style
    };
    return <button style={buttonStyle} {...props} />;
};

type ModalTriggerProps = React.HTMLAttributes<HTMLElement> & {
    style?: CSSProperties;
    as?: "span" | "div";
};

export const ModalTrigger = ({
    style,
    as: Tag = "span",
    ...props
}: ModalTriggerProps) => (
    <Tag style={{ cursor: "pointer", ...style }} {...props} />
);
